import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
    METRIC_LABELS,
    METRIC_UNITS,
    METRIC_DESCRIPTIONS,
    INVERSE_METRICS,
    DEFAULT_WEIGHTS,
} from "../../../../constants/performance";
import { NeoBadge, NeoButton, NeoInput } from "../../../../components/Common";

/**
 * GoalsForm — Formulário de metas e pesos para um grupo de cargo.
 * Migrado 100% para o Neo Design System com NeoInput e grids puros.
 *
 * Validação: soma dos pesos DEVE = 100%.
 */
const GoalsForm = ({ metricGroup, goals, weights, onSave, saving }) => {
    const metricKeys = useMemo(() => Object.keys(DEFAULT_WEIGHTS[metricGroup] || {}), [metricGroup]);

    const [localGoals, setLocalGoals] = useState({});
    const [localWeights, setLocalWeights] = useState({});

    useEffect(() => {
        const initialGoals = { ...goals };
        const initialWeights = { ...weights };
        
        // Garante que todas as métricas do schema atual existam localmente
        Object.keys(DEFAULT_WEIGHTS[metricGroup] || {}).forEach(k => {
            if (initialGoals[k] === undefined) initialGoals[k] = 0;
            if (initialWeights[k] === undefined) initialWeights[k] = DEFAULT_WEIGHTS[metricGroup][k] || 0;
        });
        
        setLocalGoals(initialGoals);
        setLocalWeights(initialWeights);
    }, [goals, weights, metricGroup]);

    // Soma dos pesos em % apenas das chaves ativas no schema
    const totalWeight = useMemo(() => {
        return metricKeys.reduce((sum, key) => sum + (Number(localWeights[key]) || 0), 0);
    }, [localWeights, metricKeys]);

    const totalWeightPercent = Math.round(totalWeight * 100);
    const isWeightValid = totalWeightPercent === 100;

    const handleGoalChange = (key, value) => {
        setLocalGoals(prev => ({ ...prev, [key]: Number(value) || 0 }));
    };

    const handleWeightChange = (key, value) => {
        setLocalWeights(prev => ({ ...prev, [key]: (Number(value) || 0) / 100 }));
    };

    const handleSubmit = () => {
        if (!isWeightValid) return;
        
        // Salva apenas as chaves ativas do schema atual, removendo lixo antigo
        const cleanGoals = {};
        const cleanWeights = {};
        metricKeys.forEach(k => {
            cleanGoals[k] = localGoals[k];
            cleanWeights[k] = localWeights[k];
        });
        
        onSave(metricGroup, cleanGoals, cleanWeights);
    };

    const getIcon = (key) => {
        if (key.includes("trial") || key.includes("Trial")) return "mdi-flask-outline";
        if (key.includes("attendance") || key.includes("assiduidade")) return "mdi-account-check";
        if (key.includes("ocupacao")) return "mdi-account-group";
        if (key.includes("retention") || key.includes("churn")) return "mdi-shield-check";
        if (key.includes("session") || key.includes("substitution")) return "mdi-calendar-check";
        if (key.includes("level") || key.includes("evaluation")) return "mdi-star-check";
        if (key.includes("revenue") || key.includes("ticket")) return "mdi-currency-usd";
        if (key.includes("sales") || key.includes("Sales")) return "mdi-cart-outline";
        if (key.includes("conversion") || key.includes("Conversion")) return "mdi-swap-horizontal";
        if (key.includes("task") || key.includes("Task")) return "mdi-checkbox-marked-circle-outline";
        if (key.includes("crescimento")) return "mdi-trending-up";
        return "mdi-target";
    };

    return (
        <div>
            {/* Header com barra de total */}
            <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom flex-wrap gap-3">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted font-size-11 fw-bold text-uppercase" style={{ letterSpacing: "0.5px" }}>
                        {metricKeys.length} métricas configuráveis
                    </span>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="font-size-11 text-muted">Soma dos pesos:</span>
                        <NeoBadge
                            color={isWeightValid ? "success" : "danger"}
                            style={{ minWidth: "50px" }}
                            className="font-size-12 px-2 py-1"
                        >
                            {totalWeightPercent}%
                        </NeoBadge>
                    </div>
                    <NeoButton
                        variant="primary"
                        size="sm"
                        icon="mdi-content-save"
                        onClick={handleSubmit}
                        disabled={!isWeightValid}
                        loading={saving}
                        loadingText="Salvando..."
                    >
                        Salvar Metas
                    </NeoButton>
                </div>
            </div>

            {!isWeightValid && (
                <div className="alert alert-warning rounded-3 py-2 font-size-12 mb-3 shadow-sm d-flex align-items-center gap-2 border-0 bg-light-soft text-warning" role="alert">
                    <i className="mdi mdi-alert-circle font-size-16 text-warning"></i>
                    <span>
                        A soma dos pesos deve ser exatamente <strong>100%</strong>. Atualmente está em <strong>{totalWeightPercent}%</strong>.
                    </span>
                </div>
            )}

            {/* Métricas */}
            <div className="d-flex flex-column gap-3">
                {metricKeys.map((key) => {
                    const label = METRIC_LABELS[key] || key;
                    const unit = METRIC_UNITS[key] || "";
                    const description = METRIC_DESCRIPTIONS[key] || "";
                    const isInverse = INVERSE_METRICS.includes(key);
                    const icon = getIcon(key);
                    const goalValue = localGoals[key] || 0;
                    const weightValue = Math.round((localWeights[key] || 0) * 100);

                    return (
                        <div key={key} className="p-3 p-md-4 rounded-3 shadow-sm bg-white" style={{ border: "1.5px solid rgba(72, 39, 18, 0.12)" }}>
                            <div className="row align-items-center g-3">
                                <div className="col-12 col-md-5 mb-2 mb-md-0">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <i className={`mdi ${icon} font-size-18 text-primary`}></i>
                                        <span className="fw-bold font-size-13 text-dark">{label}</span>
                                        {isInverse && (
                                            <NeoBadge color="warning" icon="mdi-arrow-down-bold" className="ms-2" style={{ fontSize: "9px" }}>
                                                INVERSA
                                            </NeoBadge>
                                        )}
                                    </div>
                                    {description && (
                                        <div className="text-muted font-size-11 fst-italic ps-4">
                                            {description}
                                        </div>
                                    )}
                                </div>
                                <div className="col-12 col-sm-6 col-md-4 mb-2 mb-sm-0">
                                    <NeoInput
                                        label={`META ${unit ? `(${unit})` : ""}`}
                                        type="number"
                                        size="sm"
                                        value={goalValue || ""}
                                        onChange={(e) => handleGoalChange(key, e.target.value)}
                                        placeholder="Definir meta..."
                                        min={0}
                                        step={unit === "%" ? 1 : unit === "R$" ? 100 : 1}
                                        icon={unit === "R$" ? "mdi-currency-usd" : unit === "%" ? "mdi-percent" : "mdi-target"}
                                        iconRight={unit === "%"}
                                    />
                                </div>
                                <div className="col-12 col-sm-6 col-md-3">
                                    <NeoInput
                                        label="PESO (%)"
                                        type="number"
                                        size="sm"
                                        value={weightValue || ""}
                                        onChange={(e) => handleWeightChange(key, e.target.value)}
                                        placeholder="0"
                                        min={0}
                                        max={100}
                                        step={5}
                                        icon="mdi-percent"
                                        iconRight={true}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

GoalsForm.propTypes = {
    metricGroup: PropTypes.string.isRequired,
    goals: PropTypes.object,
    weights: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    saving: PropTypes.bool,
};

export default GoalsForm;
